import { apiClient } from "@/api/client";
import { AxiosError } from "axios";
import { create } from "zustand";
import {
  AdCategoryDetail,
  AdCategoryDetailData,
  AdCategoryResponse,
} from "../types/api_types";

interface CustomParameter {
  id?: string;
  key: string;
  name: string;
  type:
    | "text"
    | "number"
    | "dropdown"
    | "date"
    | "ratio"
    | "checkbox"
    | "file_upload"
    | null;
  dropdownOptions?: string[];
  setting?: "single_publisher" | "user_defined" | "multi_publisher";
  values?: string[];
  enabled?: boolean;
}

interface Parameter {
  id?: string;
  [key: string]: any;
}

interface AdInput {
  id?: string;
  name: string;
  mediaType: string;
  parameters?: Array<Parameter>;
}

interface AdType {
  id?: string;
  adInputs: Array<AdInput>;
  publisher: string | null;
  publisherId?: string;
}

interface ParameterInput {
  id?: string;
  key: string;
  settings: string;
  inputs: Array<Object>;
  enabled?: boolean;
}

export interface PublisherStore {
  adCategory: string;
  publisherType: string;
  publishers: Array<{ id?: string; name: string }>;
  adTypes: Array<AdType>;
  parametersInput: Array<ParameterInput>;

  customParameters: Array<CustomParameter>;

  parametersData: Array<any>;

  adCategoryApiData: AdCategoryResponse | null;
  adCategoryDetailData: AdCategoryDetailData | null;

  editingAdCategoryId: string;
  isEditMode: boolean;

  // Canvas Builder state for Step 5
  canvasBuilderData: any | null;
  selectedInterface: string;
  selectedAdSpecs: Array<string>;
  selectedAdInfoBlocks: Array<string>;

  resetValue: () => void;
  setEditData: (adCategoryDetailData: AdCategoryDetailData) => void;
  setAdCategory: (name: string) => void;
  setPublisherType: (type: string) => void;
  updatePublisher: (index: number, value: string) => void;
  addPublisher: () => void;
  deletePublisher: (index: number) => void;
  generateAdTypes: () => void;
  generateParametersInput: (formData?: Record<string, Array<any>>) => void;
  addAdType: (adTypeIndex: number) => void;
  removeAdType: (adTypeIndex: number, adInputIndex: number) => void;
  updateAdInput: (
    adTypeIndex: number,
    inputIndex: number,
    field: "name" | "mediaType",
    value: string
  ) => void;
  updateParameterInput: (
    key: string,
    fieldKey: string,
    value: string,
    inputIndex?: number
  ) => void;
  addParameterInput: (paramKey: string) => void;
  removeParameterInput: (paramKey: string, inputIndex: number) => void;

  // Custom Parameters Management
  addCustomParameter: (baseKey: string) => void;
  updateCustomParameter: (
    id: string,
    field: "name" | "type",
    value: string
  ) => void;
  removeCustomParameter: (id: string) => void;
  addDropdownOption: (id: string) => void;
  updateDropdownOption: (
    id: string,
    optionIndex: number,
    value: string
  ) => void;
  removeDropdownOption: (id: string, optionIndex: number) => void;

  // Custom Parameter Value/Setting Management
  setCustomParamSetting: (
    id: string,
    setting: "single_publisher" | "user_defined" | "multi_publisher"
  ) => void;
  setCustomParamValue: (id: string, value: string, index?: number) => void;
  addCustomParamValue: (id: string) => void;
  removeCustomParamValue: (id: string, idx: number) => void;

  // Toggle parameter enabled/disabled
  toggleParameterEnabled: (key: string) => void;
  toggleCustomParameterEnabled: (key: string) => void;

  //API for the Ad-Category
  fetchParametersData: () => void;
  saveAdCategoryData: (adCategoryData) => void;
  getAdCategoryData: (query?: Array<{ key: string; value: string }>) => void;
  getAdCategoryDetailData: (adCategoryId: string) => void;
  getDeletedAdCategory: () => void;

  //API for Delete the Data
  deleteAdCategory: (adCategoryId: string) => void;
  deleteAdCategoryPublisher: (adCategoryPublisherId: string) => void;
  deleteAdCategoryAdType: (adCategoryAdTypeId: string) => void;
  deleteCustomParameterApi: (customParameterId: string) => void;

  //API for Restore the Data
  restoreAdCategory: (adCategoryId: string) => void;
  restorePublisher: (publisherId: string) => void;
  restoreAdType: (adTypeId: string) => void;

  //API for Get Deleted Items
  getDeletedPublishers: (adCategoryId: string) => void;
  getDeletedAdTypes: (adCategoryId: string) => void;

  //API for the update the Data
  updateAdCategory: (step: string, adCategoryId: string, data: any) => void;

  // Check if all required parameters for a specific ad type are complete
  isAdTypeParametersComplete: (adTypeIndex: number, adInputIndex: number) => boolean;

  // Canvas Builder actions for Step 5
  fetchCanvasBuilderData: (adTypeId: string) => Promise<void>;
  saveCanvasBuilderData: (adTypeId: string, data: any) => Promise<void>;
  setSelectedInterface: (interfaceId: string) => void;
  setSelectedAdSpecs: (specs: Array<string>) => void;
  setSelectedAdInfoBlocks: (blocks: Array<string>) => void;
  addAdSpec: (spec: string) => void;
  removeAdSpec: (spec: string) => void;
  addAdInfoBlock: (block: string) => void;
  removeAdInfoBlock: (block: string) => void;
}

const updateParameterKeys = (
  parametersInput: ParameterInput[],
  oldPublisher: string,
  newPublisher: string,
  oldAdInputName?: string,
  newAdInputName?: string
): ParameterInput[] => {
  return parametersInput.map((param) => {
    const parts = param.key.split("_");
    const [publisher, adInputName, ...paramKeyParts] = parts;

    let shouldUpdate = false;
    let updatedPublisher = publisher;
    let updatedAdInputName = adInputName;

    // Update publisher if it matches
    if (publisher === oldPublisher) {
      updatedPublisher = newPublisher;
      shouldUpdate = true;
    }

    // Update ad input name if provided and matches
    if (
      oldAdInputName &&
      newAdInputName &&
      adInputName === oldAdInputName &&
      publisher === oldPublisher
    ) {
      updatedAdInputName = newAdInputName;
      shouldUpdate = true;
    }

    if (shouldUpdate) {
      const newKey = `${updatedPublisher}_${updatedAdInputName}_${paramKeyParts.join(
        "_"
      )}`;
      return { ...param, key: newKey };
    }

    return param;
  });
};

export const useAdCategoryStore = create<PublisherStore>((set, get) => ({
  adCategory: "",
  publisherType: "",
  publishers: [],
  adTypes: [],
  parametersInput: [],
  customParameters: [],
  parametersData: [],
  adCategoryApiData: null,
  adCategoryDetailData: null,

  // Add ID for tracking edit mode
  editingAdCategoryId: null,
  isEditMode: false,

  // Canvas Builder state for Step 5
  canvasBuilderData: null,
  selectedInterface: "",
  selectedAdSpecs: [],
  selectedAdInfoBlocks: [],

  resetValue: () =>
    set({
      adCategory: "",
      publisherType: "",
      publishers: [],
      adTypes: [],
      parametersInput: [],
      customParameters: [],
      parametersData: [],
      editingAdCategoryId: null,
      isEditMode: false,
      canvasBuilderData: null,
      selectedInterface: "",
      selectedAdSpecs: [],
      selectedAdInfoBlocks: [],
    }),

  setEditData: (adCategoryDetailData: AdCategoryDetailData) => {
    const {
      id,
      name,
      publisher_type,
      publishers,
      ad_types,
      custom_parameters,
    } = adCategoryDetailData;

    // Transform ad_types to match store format with IDs
    const transformedAdTypes = ad_types.map((adType) => ({
      id: adType.id, // Store ad type ID
      publisher: adType.publisher_details.name,
      publisherId: adType.publisher_details.id, // Store publisher ID
      adInputs: adType.ad_inputs.map((input) => ({
        id: input.id, // Store ad input ID
        name: input.name,
        mediaType: input.media_type,
      })),
    }));

    // Find publishers that don't have adTypes yet
    const publishersWithAdTypes = new Set(
      ad_types.map((at) => at.publisher_details.id).filter(Boolean)
    );

    const mappedPublisherType =
      publisher_type === "single"
        ? "single_publisher"
        : publisher_type === "multi"
        ? "multiple_publisher"
        : publisher_type;

    const missingPublishers = publishers.filter(
      (publisher) => !publishersWithAdTypes.has(publisher.id)
    );

    // Create adTypes for missing publishers
    const missingAdTypes = missingPublishers.map((publisher) => ({
      publisher: mappedPublisherType === "user_defined" ? null : publisher.name,
      publisherId: publisher.id,
      adInputs: [{ name: "", mediaType: "" }],
    }));

    // Combine existing and missing adTypes
    const allAdTypes = [...transformedAdTypes, ...missingAdTypes];

    // Transform parameters to match store format with IDs
    const transformedParametersInput: ParameterInput[] = [];

    ad_types.forEach((adType) => {
      adType.ad_inputs.forEach((input) => {
        input.parameters.forEach((param) => {
          const publisherKey = adType.publisher_details.name;
          const key = `${publisherKey}_${input.name}_${param.parameter_definition.key}`;

          let settings = "";
          let inputs: any[] = [];

          if (param.value_type === "user_defined") {
            settings = "user_defined";
            inputs = [{}]; // Empty object for user-defined
          } else if (param.value_type === "single_publisher") {
            settings = "single_publisher";
            // Convert fields_json to input object
            const inputObj: any = {};
            param.fields_json.forEach((field) => {
              if (field.value !== undefined) {
                inputObj[field.key] = field.value;
              }
            });
            inputs = [inputObj];
          } else if (param.value_type === "multi_publisher") {
            settings = "multi_publisher";
            // For multi_publisher, create multiple inputs based on arrays in fields_json
            const firstField = param.fields_json[0];
            if (firstField && Array.isArray(firstField.value)) {
              const arrayLength = firstField.value.length;
              inputs = Array(arrayLength)
                .fill(0)
                .map((_, index) => {
                  const inputObj: any = {};
                  param.fields_json.forEach((field) => {
                    if (Array.isArray(field.value)) {
                      inputObj[field.key] = field.value[index] || "";
                    }
                  });
                  return inputObj;
                });
            } else {
              inputs = [{}];
            }
          }

          transformedParametersInput.push({
            id: param.id, // Store parameter ID
            key,
            settings,
            inputs,
            enabled: (param as any).enabled !== false, // Default to true if not specified
          });
        });
      });
    });

    // Transform custom parameters with IDs
    const transformedCustomParameters = (custom_parameters || []).map((cp) => ({
      id: cp.id, // Store custom parameter ID
      key: cp.key,
      name: cp.name,
      type: cp.type,
      dropdownOptions: cp.dropdownOptions,
      setting: cp.setting,
      values: cp.values,
      enabled: cp.enabled !== false, // Default to true if not specified
    }));

    set({
      adTypes: allAdTypes,
      parametersInput: transformedParametersInput,
      customParameters: transformedCustomParameters,
      editingAdCategoryId: id,
      isEditMode: true,
      adCategory: name,
      publisherType: mappedPublisherType,
      publishers: publishers.map((p) => ({ id: p.id, name: p.name })),
    });
  },

  setAdCategory: (name: string) => set({ adCategory: name }),

  setPublisherType: (type) =>
    set((state) => {
      const oldType = state.publisherType;
      const currentPublishers = state.publishers;

      // Handle single_publisher to multiple_publisher
      if (oldType === "single_publisher" && type === "multiple_publisher") {
        // Preserve the single publisher value as the first multi publisher
        return {
          publisherType: type,
          publishers: currentPublishers.length > 0 && currentPublishers[0].name
            ? currentPublishers
            : [{ name: "" }],
        };
      }

      // Handle multiple_publisher to single_publisher
      if (oldType === "multiple_publisher" && type === "single_publisher") {
        // Check if there are multiple publishers with values
        const publishersWithValues = currentPublishers.filter(p => p.name && p.name.trim() !== "");

        if (publishersWithValues.length > 1) {
          // Don't change type, throw error
          throw new Error("Please keep single Publisher");
        }

        // If only one or zero publishers, take the first one
        return {
          publisherType: type,
          publishers: currentPublishers.length > 0 ? [currentPublishers[0]] : [{ name: "" }],
        };
      }

      // Handle user_defined or other cases
      return {
        publisherType: type,
        publishers: type === "user_defined" ? [{ name: "" }] : currentPublishers.length > 0 ? currentPublishers : [{ name: "" }],
      };
    }),

  updatePublisher: (index, value) =>
    set((state) => {
      const oldPublisher = state.publishers[index]?.name;
      const updated = [...state.publishers];
      updated[index] = { ...updated[index], name: value };

      const updatedAdTypes = state.adTypes.map((adType, i) => {
        if (i === index) {
          return {
            ...adType,
            publisher: state.publisherType === "user_defined" ? null : value,
          };
        }
        return adType;
      });

      const updatedParametersInput = oldPublisher
        ? updateParameterKeys(state.parametersInput, oldPublisher, value)
        : state.parametersInput;

      return {
        publishers: updated,
        adTypes: updatedAdTypes,
        parametersInput: updatedParametersInput,
      };
    }),

  addPublisher: () =>
    set((state) => ({ publishers: [...state.publishers, { name: "" }] })),

  deletePublisher: async (index) => {
    const state = get();
    const publisher = state.publishers[index];

    // Call delete API if publisher has an id (was previously saved)
    if (publisher?.id) {
      try {
        await get().deleteAdCategoryPublisher(publisher.id);
      } catch (error) {
        throw error;
      }
    }

    set((state) => {
      const updated = state.publishers.filter((_, i) => i !== index);

      // Remove associated adTypes for this publisher
      const updatedAdTypes = state.adTypes.filter((adType, adTypeIndex) => adTypeIndex !== index);

      // Remove associated parameters for this publisher
      const publisherName = state.publishers[index]?.name;
      const updatedParametersInput = state.parametersInput.filter(param => {
        const paramPublisher = param.key.split('_')[0];
        return paramPublisher !== publisherName;
      });

      // Remove associated custom parameters for this publisher
      const updatedCustomParameters = state.customParameters.filter(param => {
        const paramPublisher = param.key.split('_')[0];
        return paramPublisher !== publisherName;
      });

      return {
        publishers: updated.length > 0 ? updated : [{ name: "" }],
        adTypes: updatedAdTypes,
        parametersInput: updatedParametersInput,
        customParameters: updatedCustomParameters
      };
    });
  },

  generateAdTypes: () => {
    set((state) => {
      const currentPublishersById = new Map(
        state.publishers.filter((p) => p.id).map((p) => [p.id, p])
      );
      const currentPublishersByName = new Map(
        state.publishers.map((p) => [p.name, p])
      );

      const validAdTypes = state.adTypes.filter((adType) => {
        if (state.publisherType === "user_defined") {
          return adType.publisher === null;
        }

        return adType.publisherId
          ? currentPublishersById.has(adType.publisherId)
          : currentPublishersByName.has(adType.publisher);
      });

      const existingPublisherIds = new Set(
        validAdTypes.map((at) => at.publisherId).filter(Boolean)
      );
      const existingPublisherNames = new Set(
        validAdTypes.map((at) => at.publisher).filter(Boolean)
      );

      const newPublishers = state.publishers.filter((publisher) => {
        if (state.publisherType === "user_defined") {
          return !validAdTypes.some((at) => at.publisher === null);
        }
        return publisher.id
          ? !existingPublisherIds.has(publisher.id)
          : !existingPublisherNames.has(publisher.name);
      });

      const newAdTypes = newPublishers.map((publisher) => ({
        publisher:
          state.publisherType === "user_defined" ? null : publisher.name,
        publisherId: publisher.id,
        adInputs: [{ name: "", mediaType: "" }],
      }));

      return {
        adTypes: [...validAdTypes, ...newAdTypes],
      };
    });
  },

  generateParametersInput: (formData?: Record<string, Array<any>>) => {
    const { adTypes, parametersInput: existingParams, parametersData } = get();

    // Use parametersData from store if formData is not provided
    const dataSource = formData || parametersData;

    const existingParamsMap = new Map(
      existingParams.map((param) => [param.key, param])
    );

    const expectedKeys = new Set<string>();
    const newParametersInput: ParameterInput[] = [];

    adTypes.forEach((adType) => {
      adType.adInputs.forEach((input) => {
        if (!input.name || !input.mediaType) return;

        const mediaGroups = dataSource[input.mediaType] || [];

        mediaGroups.forEach((group) => {
          const publisherKey = adType.publisher || "null";
          const key = `${publisherKey}_${input.name}_${group.key}`;
          expectedKeys.add(key);

          const existingParam = existingParamsMap.get(key);

          if (existingParam) {
            newParametersInput.push(existingParam);
          } else {
            const initialInput = group.fields.reduce((acc, field) => {
              return { ...acc, [field.key]: field.defaultValue ?? "" };
            }, {});

            newParametersInput.push({
              key,
              settings: "",
              inputs: [initialInput],
              enabled: true,
            });
          }
        });
      });
    });

    const filteredParameters = newParametersInput.filter((param) =>
      expectedKeys.has(param.key)
    );

    set({ parametersInput: filteredParameters });
  },

  updateParameterInput: (
    paramKey: string,
    fieldKey: string,
    value: string,
    inputIndex: number = 0
  ) => {
    set((state) => {
      const updated = [...state.parametersInput];
      const paramIndex = updated.findIndex((p) => p.key === paramKey);

      if (paramIndex === -1) return { parametersInput: updated };

      const param = { ...updated[paramIndex] };

      if (fieldKey === "settings") {
        param.settings = value;

        if (value === "user-defined") {
          const allInputs = param.inputs;

          if (allInputs.length > 1) {
            const emptyArrays: Record<string, string[]> = {};

            allInputs.forEach((input) => {
              Object.keys(input).forEach((key) => {
                if (!emptyArrays[key]) emptyArrays[key] = [];
                emptyArrays[key].push("");
              });
            });

            param.inputs = [""];
          } else {
            const input = allInputs[0] || {};
            const wrappedEmpty = Object.fromEntries(
              Object.keys(input).map((k) => [k, [""]])
            );

            param.inputs = [wrappedEmpty];
          }
        } else if (value !== "multi_publisher") {
          param.inputs = [param.inputs[0] || {}];
        }
      } else {
        const inputs = [...param.inputs];
        const currentInput = { ...(inputs[inputIndex] || {}) };
        currentInput[fieldKey] = value;
        inputs[inputIndex] = currentInput;
        param.inputs = inputs;
      }

      updated[paramIndex] = param;

      return { parametersInput: updated };
    });
  },

  addParameterInput: (paramKey: string) => {
    set((state) => {
      const updated = [...state.parametersInput];
      const paramIndex = updated.findIndex((p) => p.key === paramKey);
      if (paramIndex === -1) return { parametersInput: updated };

      const param = { ...updated[paramIndex] };

      if (param.settings !== "multi_publisher")
        return { parametersInput: updated };

      const baseKeys = Object.keys(param.inputs[0] || {}).filter(
        (k) => k !== "settings"
      );
      const newInput = Object.fromEntries(baseKeys.map((k) => [k, ""]));

      param.inputs.push(newInput);
      updated[paramIndex] = param;

      return { parametersInput: updated };
    });
  },

  removeParameterInput: (paramKey: string, inputIndex: number) => {
    set((state) => {
      const updated = [...state.parametersInput];
      const paramIndex = updated.findIndex((p) => p.key === paramKey);
      if (paramIndex === -1) return { parametersInput: updated };

      const param = { ...updated[paramIndex] };

      if (param.inputs.length <= 1) return { parametersInput: updated };

      param.inputs = param.inputs.filter((_, idx) => idx !== inputIndex);
      updated[paramIndex] = param;

      return { parametersInput: updated };
    });
  },

  addAdType: (adTypeIndex: number) => {
    set((state) => {
      const updatedAdTypes = [...state.adTypes];

      if (!updatedAdTypes[adTypeIndex]) return state;

      const targetAdType = updatedAdTypes[adTypeIndex];
      updatedAdTypes[adTypeIndex] = {
        ...targetAdType,
        adInputs: [...targetAdType.adInputs, { name: "", mediaType: "" }],
      };

      return { adTypes: updatedAdTypes };
    });
  },

  removeAdType: async (adTypeIndex: number, adInputIndex: number) => {
    const state = get();
    const targetAdType = state.adTypes[adTypeIndex];

    if (!targetAdType) return;

    const adInputToDelete = targetAdType.adInputs[adInputIndex];

    // Call delete API if ad input has an id (was previously saved)
    if (adInputToDelete?.id) {
      try {
        await get().deleteAdCategoryAdType(adInputToDelete.id);
      } catch (error) {
        throw error;
      }
    }

    set((state) => {
      const updatedAdTypes = [...state.adTypes];

      if (!updatedAdTypes[adTypeIndex]) return state;

      const targetAdType = updatedAdTypes[adTypeIndex];

      const updatedAdInputs = targetAdType.adInputs.filter(
        (_: any, idx: number) => idx !== adInputIndex
      );

      updatedAdTypes[adTypeIndex] = {
        ...targetAdType,
        adInputs: updatedAdInputs,
      };

      // Remove associated parameters for this ad type
      const publisherName = targetAdType.publisher || "null";
      const adInputName = state.adTypes[adTypeIndex].adInputs[adInputIndex]?.name;

      const updatedParametersInput = state.parametersInput.filter(param => {
        const baseKey = `${publisherName}_${adInputName}`;
        return !param.key.startsWith(baseKey + '_');
      });

      // Remove associated custom parameters for this ad type
      const updatedCustomParameters = state.customParameters.filter(param => {
        const baseKey = `${publisherName}_${adInputName}`;
        return !param.key.startsWith(baseKey + '_');
      });

      return {
        adTypes: updatedAdTypes,
        parametersInput: updatedParametersInput,
        customParameters: updatedCustomParameters
      };
    });
  },

  updateAdInput: (
    adTypeIndex: number,
    inputIndex: number,
    field: "name" | "mediaType",
    value: string
  ) => {
    set((state) => {
      const updatedAdTypes = [...state.adTypes];
      const targetAdType = updatedAdTypes[adTypeIndex];

      if (!targetAdType) return state;

      const updatedInputs = [...targetAdType.adInputs];
      const oldAdInput = updatedInputs[inputIndex];

      updatedInputs[inputIndex] = {
        ...updatedInputs[inputIndex],
        [field]: value,
      };

      updatedAdTypes[adTypeIndex] = {
        ...targetAdType,
        adInputs: updatedInputs,
      };

      // Update parametersInput keys if name changed
      let updatedParametersInput = state.parametersInput;
      if (field === "name" && oldAdInput.name !== value) {
        const publisher = targetAdType.publisher || "null";
        updatedParametersInput = updateParameterKeys(
          state.parametersInput,
          publisher,
          publisher,
          oldAdInput.name,
          value
        );
      }

      // Regenerate parameters if media type changed
      if (field === "mediaType" && oldAdInput.mediaType !== value) {
        const publisher = targetAdType.publisher || "null";
        const adInputName = oldAdInput.name;

        // Save existing parameter values before regenerating
        const existingParamValues = new Map<string, ParameterInput>();
        state.parametersInput.forEach(param => {
          const baseKey = `${publisher}_${adInputName}`;
          if (param.key.startsWith(baseKey + '_')) {
            existingParamValues.set(param.key, param);
          }
        });

        // Remove old parameters for this ad input
        updatedParametersInput = updatedParametersInput.filter(param => {
          const baseKey = `${publisher}_${adInputName}`;
          return !param.key.startsWith(baseKey + '_');
        });

        // Generate new parameters for the new media type
        const parametersData = get().parametersData;
        const mediaGroups = parametersData[value] || [];

        mediaGroups.forEach((group) => {
          const key = `${publisher}_${adInputName}_${group.key}`;

          // Check if this parameter existed with old media type
          const existingParam = existingParamValues.get(key);

          if (existingParam) {
            // Preserve the existing parameter values
            updatedParametersInput.push(existingParam);
          } else {
            // Create new parameter with default values
            const initialInput = group.fields.reduce((acc, field) => {
              return { ...acc, [field.key]: field.defaultValue ?? "" };
            }, {});

            updatedParametersInput.push({
              key,
              settings: "",
              inputs: [initialInput],
              enabled: true,
            });
          }
        });
      }

      return {
        adTypes: updatedAdTypes,
        parametersInput: updatedParametersInput,
      };
    });
  },

  addCustomParameter: (baseKey: string) =>
    set((state) => ({
      customParameters: [
        ...state.customParameters,
        {
          key: `${baseKey}_${Date.now()}-${Math.floor(Math.random() * 1000)}`,
          name: "",
          type: null,
          dropdownOptions: [],
          setting: "single_publisher",
          values: [""],
          enabled: true,
        },
      ],
    })),

  updateCustomParameter: (key: string, field: "name" | "type", value: string) =>
    set((state) => {
      const updated = state.customParameters.map((param) => {
        if (param.key !== key) return param;

        if (field === "type") {
          const updatedParam = {
            ...param,
            type: value as any,
          };

          if (value !== "dropdown") {
            updatedParam.dropdownOptions = [];
          } else if (
            value === "dropdown" &&
            !updatedParam.dropdownOptions?.length
          ) {
            updatedParam.dropdownOptions = [""];
            updatedParam.values = [""];
          }

          if (value === "checkbox") {
            updatedParam.values = ["false"];
          } else if (value !== "dropdown") {
            updatedParam.values = [""];
          }

          return updatedParam;
        }

        return { ...param, [field]: value };
      });

      return { customParameters: updated };
    }),

  updateDropdownOption: (key: string, optionIndex: number, value: string) =>
    set((state) => {
      const updated = state.customParameters.map((param) => {
        if (param.key !== key || param.type !== "dropdown") return param;

        const dropdownOptions = [...(param.dropdownOptions || [])];
        const oldValue = dropdownOptions[optionIndex];
        dropdownOptions[optionIndex] = value;

        let updatedValues = [...(param.values || [])];
        while (updatedValues.length <= optionIndex) {
          updatedValues.push("");
        }
        updatedValues[optionIndex] = value;

        return { ...param, dropdownOptions, values: updatedValues };
      });

      return { customParameters: updated };
    }),

  addDropdownOption: (key: string) =>
    set((state) => {
      const updated = state.customParameters.map((param) => {
        if (param.key !== key || param.type !== "dropdown") return param;

        const newOptions = [...(param.dropdownOptions || []), ""];
        const updatedValues = [...(param.values || []), ""];

        return {
          ...param,
          dropdownOptions: newOptions,
          values: updatedValues,
        };
      });

      return { customParameters: updated };
    }),

  removeDropdownOption: (key: string, optionIndex: number) =>
    set((state) => {
      const updated = state.customParameters.map((param) => {
        if (param.key !== key || param.type !== "dropdown") return param;

        const options = [...(param.dropdownOptions || [])];
        const filteredOptions = options.filter((_, i) => i !== optionIndex);

        let updatedValues = [...(param.values || [])];
        updatedValues = updatedValues.filter((_, i) => i !== optionIndex);

        if (updatedValues.length === 0) {
          updatedValues = [""];
        }

        return {
          ...param,
          dropdownOptions: filteredOptions,
          values: updatedValues,
        };
      });

      return { customParameters: updated };
    }),

  removeCustomParameter: async (key: string) => {
    const state = get();
    const paramToDelete = state.customParameters.find(param => param.key === key);

    // Call delete API if custom parameter has an id (was previously saved)
    if (paramToDelete?.id) {
      try {
        await get().deleteCustomParameterApi(paramToDelete.id);
      } catch (error) {
        throw error;
      }
    }

    set((state) => ({
      customParameters: state.customParameters.filter(
        (param) => param.key !== key
      ),
    }));
  },

  setCustomParamSetting: (
    key: string,
    setting: "single_publisher" | "user_defined" | "multi_publisher"
  ) =>
    set((state) => ({
      customParameters: state.customParameters.map((param) => {
        if (param.key !== key) return param;

        let newValues = param.values || [""];

        if (setting === "multi_publisher" && newValues.length === 0) {
          newValues = [""];
        } else if (setting !== "multi_publisher" && newValues.length > 1) {
          newValues = [newValues[0] || ""];
        }

        return {
          ...param,
          setting,
          values: newValues,
        };
      }),
    })),

  setCustomParamValue: (key: string, value: string, index: number = 0) =>
    set((state) => ({
      customParameters: state.customParameters.map((param) => {
        if (param.key !== key) return param;
        const values = [...(param.values || [""])];

        while (values.length <= index) {
          values.push("");
        }

        values[index] = value;
        return { ...param, values };
      }),
    })),

  addCustomParamValue: (key: string) =>
    set((state) => ({
      customParameters: state.customParameters.map((param) =>
        param.key === key
          ? { ...param, values: [...(param.values || [""]), ""] }
          : param
      ),
    })),

  removeCustomParamValue: (key: string, idx: number) =>
    set((state) => ({
      customParameters: state.customParameters.map((param) => {
        if (param.key !== key) return param;
        const values = (param.values || []).filter((_, i) => i !== idx);
        return { ...param, values: values.length ? values : [""] };
      }),
    })),

  toggleParameterEnabled: (key: string) =>
    set((state) => ({
      parametersInput: state.parametersInput.map((param) =>
        param.key === key
          ? { ...param, enabled: param.enabled === false ? true : false }
          : param
      ),
    })),

  toggleCustomParameterEnabled: (key: string) =>
    set((state) => ({
      customParameters: state.customParameters.map((param) =>
        param.key === key
          ? { ...param, enabled: param.enabled === false ? true : false }
          : param
      ),
    })),

  fetchParametersData: async () => {
    try {
      const parameterResponse = await apiClient.get(`/parameter-definitions`);
      const data = parameterResponse.data;
      set({
        parametersData: data,
      });
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.message || "Something went wrong"
        : "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  saveAdCategoryData: async (adCategoryData) => {
    try {
      const adCategoryResponse = await apiClient.post(
        `/ad-categories`,
        adCategoryData
      );
      const data = adCategoryResponse.data;
      get().resetValue();
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.message || "Something went wrong"
        : "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  deleteAdCategory: async (adCategoryId: string) => {
    try {
      const adCategoryResponse = await apiClient.delete(
        `/ad-categories/${adCategoryId}`
      );
      const data = adCategoryResponse.data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.message || "Something went wrong"
        : "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  deleteAdCategoryPublisher: async (adCategoryPublisherId: string) => {
    try {
      const adCategoryResponse = await apiClient.delete(
        `/publishers/${adCategoryPublisherId}`
      );
      const data = adCategoryResponse.data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.message || "Something went wrong"
        : "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  deleteAdCategoryAdType: async (adCategoryAdTypeId: string) => {
    try {
      const adCategoryResponse = await apiClient.delete(
        `/ad-types/${adCategoryAdTypeId}`
      );
      const data = adCategoryResponse.data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.message || "Something went wrong"
        : "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  deleteCustomParameterApi: async (customParameterId: string) => {
    try {
      const response = await apiClient.delete(
        `/custom-parameters/${customParameterId}`
      );
      const data = response.data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.message || "Something went wrong"
        : "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  restoreAdCategory: async (adCategoryId: string) => {
    try {
      const adCategoryResponse = await apiClient.post(
        `/ad-categories/${adCategoryId}/restore`,
        {}
      );
      const data = adCategoryResponse.data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.message || "Something went wrong"
        : "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  restorePublisher: async (publisherId: string) => {
    try {
      const response = await apiClient.put(
        `/publishers/${publisherId}/restore`
      );
      const data = response.data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.message || "Failed to restore publisher"
        : "Failed to restore publisher";
      throw new Error(errorMessage);
    }
  },

  restoreAdType: async (adTypeId: string) => {
    try {
      const response = await apiClient.put(
        `/ad-types/${adTypeId}/restore`
      );
      const data = response.data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.message || "Failed to restore ad type"
        : "Failed to restore ad type";
      throw new Error(errorMessage);
    }
  },

  getDeletedPublishers: async (adCategoryId: string) => {
    try {
      const response = await apiClient.get(
        `/ad-categories/${adCategoryId}/publishers/deleted`
      );
      const data = response.data;
      set({
        adCategoryApiData: data,
      });
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.message || "Failed to fetch deleted publishers"
        : "Failed to fetch deleted publishers";
      throw new Error(errorMessage);
    }
  },

  getDeletedAdTypes: async (adCategoryId: string) => {
    try {
      const response = await apiClient.get(
        `/ad-categories/${adCategoryId}/ad-types/deleted`
      );
      const data = response.data;
      set({
        adCategoryApiData: data,
      });
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.message || "Failed to fetch deleted ad types"
        : "Failed to fetch deleted ad types";
      throw new Error(errorMessage);
    }
  },

  updateAdCategoryData: async (adCategoryId: string, adCategoryData) => {
    try {
      const adCategoryResponse = await apiClient.put(
        `/ad-categories/${adCategoryId}`,
        adCategoryData
      );
      const data = adCategoryResponse.data;
      get().resetValue();
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.message || "Something went wrong"
        : "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  getAdCategoryData: async (query?: Array<{ key: string; value: string }>) => {
    try {
      let url = `/ad-categories`;

      if (query && query.length > 0) {
        const params = new URLSearchParams();
        query.forEach(({ key, value }) => {
          if (key && value) {
            params.append(key, value);
          }
        });
        url += `?${params.toString()}`;
      }

      const adCategoryResponse = await apiClient.get(url);
      const data = adCategoryResponse.data;
      set({
        adCategoryApiData: data,
      });
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.message || "Something went wrong"
        : "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  getDeletedAdCategory: async () => {
    try {
      const adCategoryResponse = await apiClient.get(`/deleted-ad-categories`);
      const data = adCategoryResponse.data;
      set({
        adCategoryApiData: data,
      });
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.message || "Something went wrong"
        : "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  getAdCategoryDetailData: async (adCategoryId) => {
    try {
      const adCategoryResponse = await apiClient.get<AdCategoryDetail>(
        `/ad-categories/${adCategoryId}`
      );
      const data = adCategoryResponse.data.data;
      set({
        adCategoryDetailData: data,
      });
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.message || "Something went wrong"
        : "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  //API for the update the Data
  updateAdCategory: async (step: string, adCategoryId: string, data: any) => {
    try {
      const adCategoryResponse = await apiClient.put<AdCategoryDetail>(
        `/ad-categories/update/${adCategoryId}?step=${step}`,
        data
      );
      const responseData = adCategoryResponse.data.data;
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.message || "Something went wrong"
        : "Something went wrong";
      throw new Error(errorMessage);
    }
  },

  isAdTypeParametersComplete: (adTypeIndex: number, adInputIndex: number) => {
    const state = get();
    const adType = state.adTypes[adTypeIndex];
    if (!adType) return false;
    const adInput = adType.adInputs[adInputIndex];
    if (!adInput || !adInput.name || !adInput.mediaType) return false;

    const baseKey = `${adType.publisher}_${adInput.name}`;
    const mediaParams = state.parametersData[adInput.mediaType] || [];

    // Check all standard parameters for this ad type
    for (const group of mediaParams) {
      const parameterKey = `${baseKey}_${group.key}`;
      const paramObj = state.parametersInput.find((p) => p.key === parameterKey);
      if (!paramObj) continue;
      if (paramObj.enabled === false) continue;

      if (!paramObj.settings || paramObj.settings.trim() === "") return false;

      if (paramObj.settings !== "user_defined") {
        for (const input of paramObj.inputs) {
          for (const field of group.fields) {
            const value = (input as any)[field.key];
            if (!value || value.toString().trim() === "") return false;
          }
        }
      }

      if (paramObj.settings === "multi_publisher" && paramObj.inputs.length === 0) return false;
    }

    // Check custom parameters for this ad type
    const customParams = state.customParameters.filter(
      (e) => e.key.split("_")[0] + "_" + e.key.split("_")[1] === baseKey
    );
    for (const cp of customParams) {
      if (cp.enabled === false) continue;
      if (!cp.name || cp.name.trim() === "") return false;
      if (!cp.type) return false;
      if (!cp.setting) return false;

      if (cp.type === "dropdown" && (!cp.dropdownOptions || cp.dropdownOptions.length === 0)) return false;

      if (cp.setting !== "user_defined") {
        if (cp.setting === "single_publisher") {
          const hasValue = cp.values && cp.values.some((v) => v && v.toString().trim() !== "");
          if (!hasValue) return false;
        }
        if (cp.setting === "multi_publisher" && (!cp.values || cp.values.length === 0)) return false;
      }
    }

    return true;
  },

  // Canvas Builder actions for Step 5
  fetchCanvasBuilderData: async (adTypeId: string) => {
    try {
      const response = await apiClient.get(`/ad-types/${adTypeId}/canvas-builder`);
      const data = response.data;
      set({
        canvasBuilderData: data.data,
      });
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.message || "Failed to fetch canvas builder data"
        : "Failed to fetch canvas builder data";
      throw new Error(errorMessage);
    }
  },

  saveCanvasBuilderData: async (adTypeId: string, data: any) => {
    try {
      const response = await apiClient.post(
        `/ad-types/${adTypeId}/canvas-builder`,
        data
      );
      const responseData = response.data;
      set({
        canvasBuilderData: responseData.data,
      });
    } catch (error) {
      const axiosError = error as AxiosError<any>;
      const errorMessage = axiosError.response
        ? axiosError.response.data?.message || "Failed to save canvas builder data"
        : "Failed to save canvas builder data";
      throw new Error(errorMessage);
    }
  },

  setSelectedInterface: (interfaceId: string) => set({ selectedInterface: interfaceId }),

  setSelectedAdSpecs: (specs: Array<string>) => set({ selectedAdSpecs: specs }),

  setSelectedAdInfoBlocks: (blocks: Array<string>) => set({ selectedAdInfoBlocks: blocks }),

  addAdSpec: (spec: string) =>
    set((state) => ({
      selectedAdSpecs: [...state.selectedAdSpecs, spec],
    })),

  removeAdSpec: (spec: string) =>
    set((state) => ({
      selectedAdSpecs: state.selectedAdSpecs.filter((s) => s !== spec),
    })),

  addAdInfoBlock: (block: string) =>
    set((state) => ({
      selectedAdInfoBlocks: [...state.selectedAdInfoBlocks, block],
    })),

  removeAdInfoBlock: (block: string) =>
    set((state) => ({
      selectedAdInfoBlocks: state.selectedAdInfoBlocks.filter((b) => b !== block),
    })),
}));
